import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { PropertyData } from "@store/search-board/search-board.model";
import { SpinnerOverlayService } from "@shared/services/spinner-overlay.service";
import { ActionName } from "@shared/models/effects-names.model";
import { ModalType } from "@shared/models/modal.model";

import type { Swiper } from "swiper/types";

type SwiperElement = HTMLElement & {
  initialize: () => void;
  swiper: Swiper;
};
type SwiperEvent = CustomEvent<[Swiper]>;

@Component({
  selector: "rlst-picture-viewer",
  templateUrl: "./picture-viewer.component.html",
  styleUrls: ["./picture-viewer.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PictureViewerComponent implements OnDestroy, OnInit {
  @ViewChild("swiper", { static: false }) swiper?: ElementRef<SwiperElement>;
  @ViewChild("swiperGalleryThumbs", { static: false })
  public swiperGalleryThumbs?: ElementRef<SwiperElement>;
  public disablePrevSlide: boolean;
  public disableNextSlide: boolean;
  public disablePrevThumbsSlide: boolean;
  public disableNextThumbsSlide: boolean;
  public activeSlideIndex: number;
  private activeClass: string = "swiper-slide-thumb-active";
  private pictureViewerSpinnerVisible = false;
  private viewerRevealed = false;

  private get mainSwiper(): Swiper {
    return this.swiper?.nativeElement.swiper as Swiper;
  }

  private get thumbnailSwiper(): Swiper {
    return this.swiperGalleryThumbs?.nativeElement.swiper as Swiper;
  }

  constructor(
    private readonly dialogRef: MatDialogRef<PictureViewerComponent>,
    private readonly spinnerOverlayService: SpinnerOverlayService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      imageSource: Array<string>;
      isMobile: boolean;
      propertyData: PropertyData;
    }
  ) {}

  slideNext() {
    this.mainSwiper.slideNext(100);
    this.thumbnailSwiper.slideTo(
      this.mainSwiper.activeIndex
    );
    this.cdr.markForCheck();
  }
  slidePrev() {
    this.mainSwiper.slidePrev(100);
    this.thumbnailSwiper.slideTo(
      this.mainSwiper.activeIndex
    );
    this.cdr.markForCheck();
  }

  slideNextGalleryThumbs() {
    this.thumbnailSwiper.slideNext(100);
    this.cdr.markForCheck();
  }
  slidePrevGalleryThumbs() {
    this.thumbnailSwiper.slidePrev(100);
    this.cdr.markForCheck();
  }

  onSlideChange() {
    const mainSwiper = this.swiper?.nativeElement.swiper;
    const thumbnailSwiper = this.swiperGalleryThumbs?.nativeElement.swiper;
    if (!mainSwiper || !thumbnailSwiper) {
      return;
    }

    const previousSlide = thumbnailSwiper.slides[this.activeSlideIndex];
    if (previousSlide?.classList.contains(this.activeClass)) {
      previousSlide.classList.remove(this.activeClass);
    }
    const activeSlide = thumbnailSwiper.slides[mainSwiper.activeIndex];
    if (activeSlide && !activeSlide.classList.contains(this.activeClass)) {
      activeSlide.classList.add(this.activeClass);
      this.activeSlideIndex = mainSwiper.activeIndex;
    }
    this.disablePrevSlide = mainSwiper.isBeginning;
    this.disableNextSlide = mainSwiper.isEnd;
    this.disablePrevThumbsSlide =
      thumbnailSwiper.isBeginning;
    this.disableNextThumbsSlide = thumbnailSwiper.isEnd;
    this.cdr.markForCheck();
  }

  goToSlide(index: number) {
    this.mainSwiper.slideTo(index);
    this.thumbnailSwiper.slideTo(index);
    this.cdr.markForCheck();
  }

  onMainSwiperReady(event: SwiperEvent): void {
    const [swiper] = event.detail;
    this.disablePrevSlide = swiper.isBeginning;
    this.disableNextSlide = swiper.isEnd;
    this.revealViewer();
    this.cdr.markForCheck();
  }

  onThumbnailSwiperReady(event: SwiperEvent): void {
    const [swiper] = event.detail;
    this.disablePrevThumbsSlide = swiper.isBeginning;
    this.disableNextThumbsSlide = swiper.isEnd;
    this.activeSlideIndex = swiper.activeIndex;
    swiper.slides[swiper.activeIndex]?.classList.add(this.activeClass);
    this.cdr.markForCheck();
  }

  ngOnInit() {
    if (this.data.isMobile) {
      return;
    }
    this.spinnerOverlayService.showSpinner(
      ActionName.LOAD_IMAGES_ON_PICTURE_VIEWER
    );
    this.pictureViewerSpinnerVisible = true;
  }

  ngAfterViewInit(): void {
    this.swiper?.nativeElement.initialize();
    this.swiperGalleryThumbs?.nativeElement.initialize();
  }

  ngOnDestroy() {
    this.hidePictureViewerSpinner();
    this.thumbnailSwiper?.destroy();
    this.mainSwiper?.destroy();
    this.swiperGalleryThumbs = null;
    this.swiper = null;
  }

  private revealViewer(): void {
    if (!this.viewerRevealed) {
      this.dialogRef.removePanelClass(ModalType.HIDDEN);
      this.dialogRef.updateSize("auto", "auto");
      this.viewerRevealed = true;
    }
    this.hidePictureViewerSpinner();
  }

  private hidePictureViewerSpinner(): void {
    if (this.pictureViewerSpinnerVisible) {
      this.spinnerOverlayService.hideSpinner(
        ActionName.LOAD_IMAGES_ON_PICTURE_VIEWER
      );
      this.pictureViewerSpinnerVisible = false;
    }
  }

  getCounterLabel() {
    return `${this.swiper ? this.mainSwiper.activeIndex + 1 : 1}/${
      this.data.imageSource.length
    } photos`;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  printPhotos() {
    this.dialogRef.close({
      propertyData: this.data.propertyData,
    });
  }
}
